import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCity } from '../context/CityContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSiteSettings } from '../context/SiteSettingsContext';
import './Nav.css';

export default function Nav() {
  const { user, logout } = useAuth();
  const { cities, selectedCity, setSelectedCity } = useCity();
  const handleLogout = async () => { try { await logout(); } catch {} };
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [cityMenuOpen, setCityMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState('');
  const cityRef = useRef(null);
  const { blogEnabled } = useSiteSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  // Close city dropdown on outside click
  useEffect(() => {
    if (!cityMenuOpen) return;
    const handler = (e) => {
      if (cityRef.current && !cityRef.current.contains(e.target)) setCityMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [cityMenuOpen]);

  const handleSearch = e => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <>
      <nav id="nav" className={scrolled ? 'scrolled' : ''}>
        <Link to="/" className="logo">
          <img
            src="/logo-white.png"
            alt="Zénde"
            className="logo-img"
            style={{ height: 30, width: 'auto', display: 'block' }}
          />
        </Link>

        <div className="nav-mid">
          <Link to="/places" className={`nav-link ${location.pathname.startsWith('/places') ? 'active' : ''}`}>Places</Link>
          <Link to="/events"  className={`nav-link ${location.pathname.startsWith('/events')  ? 'active' : ''}`}>Events</Link>
          <Link to="/activities" className={`nav-link ${location.pathname.startsWith('/activities') ? 'active' : ''}`}>Activities</Link>
          {/* ITINERARY_DISABLED <Link to="/plan" className={`nav-link ${location.pathname.startsWith('/plan') ? 'active' : ''}`}>Plan</Link> */}
          {blogEnabled && <Link to="/blog" className={`nav-link ${location.pathname.startsWith('/blog') ? 'active' : ''}`}>Blog</Link>}
        </div>
        
        <div className="nav-right ">
          {/* City selector */}
          <div className="nav-city-wrap" ref={cityRef}>
            <button className={`nav-city${cityMenuOpen ? ' open' : ''}`} onClick={() => setCityMenuOpen(v => !v)}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="5" r="3" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M6 1v1M6 9v1M1 5h1M9 5h1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              {selectedCity || 'All cities'}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.6, marginLeft: 2 }}>
                <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </button>
            {cityMenuOpen && cities.length > 0 && (
              <div className="nav-city-menu">
                <button
                  className={`ncm-item ${!selectedCity ? 'ncm-active' : ''}`}
                  onClick={() => { setSelectedCity(''); setCityMenuOpen(false); }}>
                  All cities
                </button>
                {cities.map(c => (
                  <button key={c.id}
                    className={`ncm-item ${selectedCity === c.name ? 'ncm-active' : ''}`}
                    onClick={() => { setSelectedCity(c.name); setCityMenuOpen(false); }}>
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {user ? (
            <div style={{position:'relative'}}>
              <button className="nav-user-btn" onClick={()=>setUserMenuOpen(v=>!v)}>
                <span className="nav-user-av">
                  <img height={20} width={20}
                    src={
                      user.avatar ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}`
                    }
                    alt="avatar"
                  />
                </span>
                <span style={{fontSize:13,fontWeight:500}}>{user.name?.split(' ')[0]}</span>
              </button>
              {userMenuOpen && (
                <div className="nav-user-menu" onMouseLeave={()=>setUserMenuOpen(false)}>
                  <div className="num-label">Signed in as</div>
                  <div className="num-email">{user.email}</div>
                  <div className="num-divider"/>
                  <button className="num-item" onClick={()=>{navigate('/profile');setUserMenuOpen(false);}}>My profile</button>
                  <button className="num-item" onClick={()=>{navigate('/profile?tab=Saved');setUserMenuOpen(false);}}>Saved places</button>
                  {/* ITINERARY_DISABLED <button className="num-item" onClick={()=>{navigate('/plan');setUserMenuOpen(false);}}>Planner</button> */}
                  <button className="num-item" onClick={()=>{handleLogout();setUserMenuOpen(false);}}>Sign out</button>
                </div>
              )}
            </div>
          ) : (
            <button className="nav-btn" onClick={() => navigate('/login')}>Sign in</button>
          )}
          {/* <button className="nav-btn" onClick={() => navigate('/plan')} style={{background:'var(--o)',borderColor:'var(--o)'}}>Plan a trip</button> */}
        </div>
        
        <button className="ham" onClick={() => setMenuOpen(v => !v)} aria-label="Menu">
          <span /><span /><span />
        </button>
      </nav>

      {/* Mobile menu */}
      <div className={`mob-menu ${menuOpen ? 'open' : ''}`}>
        <Link to="/places">Places</Link>
        <Link to="/events">Events</Link>
        <Link to="/activities">Activities</Link>
        {/* ITINERARY_DISABLED <Link to="/plan">Plan a trip</Link> */}
        {/* <Link to="/cities">Cities</Link> */}
        {/* <Link to="/contact">Contact</Link> */}
        {user ? (
          <>
            <Link to="/profile">My profile</Link>
            <button onClick={()=>{handleLogout();setMenuOpen(false);}} style={{background:'none',border:'1px solid rgba(255,255,255,.2)',color:'rgba(255,255,255,.7)',padding:'12px',borderRadius:'var(--r)',cursor:'pointer',fontSize:14,textAlign:'left',fontFamily:'var(--ui)'}}>Sign out ({user.name?.split(' ')[0]})</button>
          </>
        ) : <Link to="/login">Sign in / Create account</Link>}
        <form onSubmit={handleSearch} className="mob-search">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" />
          <button type="submit">→</button>
        </form>
      </div>
    </>
  );
}
